import React, { useCallback } from 'react';
import { Box, Grid, Text } from '@chakra-ui/react';
import { styles } from './common-date-picker.styles';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import { Button as ChakraButton, ButtonProps as ChakraButtonProps } from '@chakra-ui/react';

export type CommonDatePickerHeaderProps = {
  dayPicker?: boolean;
  isClearBtnDisabled?: boolean;
  date?: any;
  monthPicker?: boolean;
  onMonthClick?: () => void;
  onMonthPickerYearClick?: () => void;
  onLeftArrowClick?: () => void;
  onRightArrowClick?: () => void;
  onYearClick?: () => void;
  yearPicker?: boolean;
  selectedYear?: string;
  yearRange?: string;
};

enum ButtonVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  LINKS = 'links'
}

enum ButtonSize {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg'
}

type ButtonProps = {
  size?: ButtonSize;
  label: string;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  variant?: ButtonVariant;
  rightIcon?: React.ReactElement;
  leftIcon?: React.ReactElement;
  className?: string;
  color?: string;
  backgroundColor?: string;
  dataTestId?: string;
  'data-dd-action-name'?: string;
} & ChakraButtonProps;

const Button: React.FC<ButtonProps> = ({
  backgroundColor,
  color,
  'data-dd-action-name': rawDataActionNameForDD,
  dataTestId,
  label,
  leftIcon,
  rightIcon,
  className = '',
  disabled = false,
  size = ButtonSize.MD,
  variant = ButtonVariant.PRIMARY,
  ...props
}) => {
  // we have confirmed that all Buttons are not carrying PHI
  // "~" at the beginning signs the action so it will not be redacted
  // we usually draw from labels, but if we don't want to double ~~ if
  // they switch
  const verifiedActionNameForDD = rawDataActionNameForDD || label;
  const signedActionNameForDD = verifiedActionNameForDD.startsWith('~')
    ? verifiedActionNameForDD
    : `~${verifiedActionNameForDD}`;
  return (
    <ChakraButton
      {...props}
      variant={variant}
      disabled={disabled}
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      size={size}
      className={className}
      color={color}
      backgroundColor={backgroundColor}
      data-testid={dataTestId?.trim() ?? 'button'}
      data-dd-action-name={signedActionNameForDD}>
      {label}
    </ChakraButton>
  );
};

Button.displayName = 'Button';

const CommonDatePickerHeader: React.FC<CommonDatePickerHeaderProps> = props => {
  const {
    onMonthClick,
    onMonthPickerYearClick,
    onYearClick,
    onRightArrowClick,
    onLeftArrowClick,
    date,
    dayPicker,
    monthPicker,
    yearPicker,
    yearRange,
    selectedYear
  } = props;

  const classes = styles();

  const getMonthAndYear = useCallback(() => {
    return (
      date
        ?.toLocaleString('en-US', {
          month: 'long',
          year: 'numeric'
        })
        .split(' ') ?? null
    );
  }, [date]);

  const [month, year] = getMonthAndYear() ?? [];

  return (
    <div className="common-header-date-picker">
      <Box sx={classes.root}>
        <Grid sx={classes.header}>
          <Grid sx={classes.title}>
            {dayPicker && (
              <>
                <Text
                  className="react-datepicker__current-month"
                  style={classes.monthIcon}
                  onClick={onMonthClick}>
                  {month}
                </Text>
                <Text
                  className="react-datepicker__current-month"
                  style={classes.icon}
                  onClick={onYearClick}>
                  {year}
                </Text>
              </>
            )}
            {yearPicker && (
              <Text className="react-datepicker__current-month">
                {yearRange}
              </Text>
            )}
            {monthPicker && (
              <Text
                className="react-datepicker__current-month"
                onClick={onMonthPickerYearClick}
                style={classes.icon}>
                {selectedYear}
              </Text>
            )}
          </Grid>
        </Grid>
        <div className="arrow-buttons">
          <Box onClick={onLeftArrowClick}>
            <BiomeIcon
              iconName={IconName.Chevron_Left}
              iconClass={`${classes.icon}`}
              iconSize="20"
            />
          </Box>
          <Box onClick={onRightArrowClick}>
            <BiomeIcon
              iconName={IconName.Chevron_Right}
              iconSize="20"
              iconClass={`${classes.icon}`}
            />
          </Box>
        </div>
      </Box>
    </div>
  );
};

CommonDatePickerHeader.displayName = 'CommonDatePickerHeader';
export { CommonDatePickerHeader };
